import { StatementArea, type TrialBalanceDetail, type TrialBalanceLineView } from '@/core/models/trial-balance.models';

/** One account line of the read-only trial balance view. */
export interface TbViewLine {
  lineNo: number;
  code: string;
  /** The chart's name for the account (what the account is called), or the particular when the account is not in the chart. */
  name: string;
  /** What the line itself says, only when it differs from the account's name. */
  particular: string;
  debit: number;
  credit: number;
}

/** The lines of one account type (category), with their subtotal. */
export interface TbViewGroup {
  key: string;
  title: string;
  lines: TbViewLine[];
  debit: number;
  credit: number;
}

/** Profit and loss, Balance sheet or the accounts the chart does not place. */
export interface TbViewSection {
  key: string;
  title: string;
  groups: TbViewGroup[];
  debit: number;
  credit: number;
}

const round2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

const SECTION_ORDER: { area: number | null; key: string; title: string }[] = [
  { area: StatementArea.ProfitAndLoss, key: 'pl', title: 'Profit and loss' },
  { area: StatementArea.BalanceSheet, key: 'bs', title: 'Balance sheet' },
  { area: StatementArea.Equity, key: 'eq', title: 'Equity' },
  { area: null, key: 'other', title: 'Not classified' },
];

const byCode = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });

/** A line with no account and no amount is an empty row of the editor's grid, not part of the trial balance. */
const isBlank = (line: TrialBalanceLineView) =>
  !`${line.accountCode ?? ''}`.trim() && !Number(line.debit) && !Number(line.credit);

/**
 * APT366: a saved trial balance laid out like a printed statement - the lines under Profit and loss / Balance sheet, then under
 * their account type, each group with a subtotal, in account code order. Nothing is recalculated for the totals: the page shows
 * the server's (`totalDebit`, `totalCredit`), these section and group figures are only the lines added up.
 */
export function buildTrialBalanceView(detail: Pick<TrialBalanceDetail, 'lines' | 'accounts'>): TbViewSection[] {
  const accounts = new Map((detail.accounts ?? []).map((a) => [`${a.code}`.trim(), a]));
  const sections = new Map<string, Map<string, TbViewGroup>>();

  for (const line of detail.lines ?? []) {
    if (isBlank(line)) continue;
    const code = `${line.accountCode ?? ''}`.trim();
    const account = accounts.get(code);
    const area = line.statementArea ?? account?.statementArea ?? null;
    const section = SECTION_ORDER.find((s) => s.area === area) ?? SECTION_ORDER[SECTION_ORDER.length - 1];
    const groupTitle = account?.accountType?.trim() || 'Other accounts';
    const name = account?.name?.trim() || line.particular?.trim() || '';
    const own = line.particular?.trim() ?? '';
    const particular = own && own.toLowerCase() !== name.toLowerCase() ? own : '';

    const groups = sections.get(section.key) ?? new Map<string, TbViewGroup>();
    sections.set(section.key, groups);
    const group = groups.get(groupTitle) ?? { key: `${section.key}:${groupTitle}`, title: groupTitle, lines: [], debit: 0, credit: 0 };
    groups.set(groupTitle, group);
    group.lines.push({ lineNo: line.lineNo, code, name, particular, debit: Number(line.debit) || 0, credit: Number(line.credit) || 0 });
  }

  return SECTION_ORDER.filter((s) => sections.has(s.key)).map((s) => {
    const groups = [...sections.get(s.key)!.values()].map((g) => {
      const lines = [...g.lines].sort((a, b) => byCode(a.code, b.code) || a.lineNo - b.lineNo);
      return {
        ...g,
        lines,
        debit: round2(lines.reduce((sum, l) => sum + l.debit, 0)),
        credit: round2(lines.reduce((sum, l) => sum + l.credit, 0)),
      };
    });
    groups.sort((a, b) => byCode(a.lines[0].code, b.lines[0].code));
    return {
      key: s.key,
      title: s.title,
      groups,
      debit: round2(groups.reduce((sum, g) => sum + g.debit, 0)),
      credit: round2(groups.reduce((sum, g) => sum + g.credit, 0)),
    };
  });
}
